import { ChakraProvider } from '@chakra-ui/react';
import { lazy, Suspense } from 'react';
import { createBrowserRouter, Outlet, RouterProvider } from 'react-router-dom';
import { BaseTheme } from '../assets/style/theme/BaseTheme';
import { InitLoading } from '../components/Layouts/InitLoading';
import { UserLayout } from '../components/Layouts/UserLayout';
import { Preloader } from '../components/Preloader';
import { UIProvider } from '../context';
import { ROUTERS, STATIC_BASE_ROUTE, USER_BASE_ROUTE } from '../data/routers';

//──── Authorized user data ───────────────────────────────────────────────────────────────
import UserScoreCard from '../pages/user/UserScoreCard';
import UserMembers from '../pages/user/UserMembers';
import UserCreateAndEditMember from '../pages/user/CreateAndEditMember';
import UserMemberToUser from '../pages/user/MemberTransitionToUser';
import UserProfile from '../pages/user/UserProfile';
import UserQrCode from '../pages/user/UserQrCode';
import UserDeletedSuccess from '../pages/user/UserDeletedSuccess';
import { ProtectedRoute } from '../components/ProtectedRoute';
import { AuthProvider } from '../context';

//──── Auth user ─────────────────────────────────────────────────────────────────────────
const Login = lazy(() => import('../pages/Login'));
const CreateProfile = lazy(() => import('../pages/CreateProfile'));
const CreateProfileSuccess = lazy(() => import('../pages/CreateProfileSuccess'));
const AddResponsiblePerson = lazy(() => import('../pages/AddResponsiblePerson'));
const FailedScreen = lazy(() => import('../pages/FailedScreen'));
const ResetPassword = lazy(() => import('../pages/ResetPassword'));
const ResetPasswordCompleted = lazy(() => import('../pages/ResetPasswordCompleted'));
const SetNewPassword = lazy(() => import('../pages/SetNewPassword'));
const SetNewPasswordSuccess = lazy(() => import('../pages/SetNewPasswordSuccess'));
const SetNewPasswordLinkExpired = lazy(() => import('../pages/SetNewPasswordLinkExpired'));
const PageNotFound = lazy(() => import('../pages/PageNotFound'));

//──── Static pages ──────────────────────────────────────────────────────────────────────
const PrivacyPolicy = lazy(() => import('../pages/static/PrivacyPolicy'));
const Disclaimer = lazy(() => import('../pages/static/Disclaimer'));
const Rules = lazy(() => import('../pages/static/Rules'));

const RootAuthRoute = () => (
  <AuthProvider>
    <Outlet />
  </AuthProvider>
);

const router = createBrowserRouter([
  {
    element: <RootAuthRoute />,
    errorElement: <PageNotFound />,
    children: [
      { path: ROUTERS.INIT, element: <Login /> },
      { path: ROUTERS.CREATE_PROFILE, element: <CreateProfile /> },
      { path: ROUTERS.CREATE_PROFILE_SUCCESS, element: <CreateProfileSuccess /> },
      { path: ROUTERS.ADD_RESPONSIBLE_PERSON, element: <AddResponsiblePerson /> },
      { path: ROUTERS.FAILED_SCREEN, element: <FailedScreen /> },
      { path: ROUTERS.RESET_PASSWORD, element: <ResetPassword /> },
      { path: ROUTERS.RESET_PASSWORD_COMPLETED, element: <ResetPasswordCompleted /> },
      { path: ROUTERS.SET_NEW_PASSWORD, element: <SetNewPassword /> },
      { path: ROUTERS.SET_NEW_PASSWORD_SUCCESS, element: <SetNewPasswordSuccess /> },
      { path: ROUTERS.SET_NEW_PASSWORD_LINK_EXPIRED, element: <SetNewPasswordLinkExpired /> },
      { path: ROUTERS.PROFILE_DELETED, element: <UserDeletedSuccess /> },
      {
        path: USER_BASE_ROUTE,
        element: (
          <ProtectedRoute>
            <UserLayout />
          </ProtectedRoute>
        ),
        children: [
          { path: ROUTERS.USER.SCORE, element: <UserScoreCard /> },
          { path: ROUTERS.USER.PROFILE, element: <UserProfile /> },
          { path: ROUTERS.USER.MEMBERS, element: <UserMembers /> },
          { path: ROUTERS.USER.CREATE_AND_EDIT_MEMBER, element: <UserCreateAndEditMember /> },
          { path: ROUTERS.USER.MEMBER_TO_USER, element: <UserMemberToUser /> },
          { path: ROUTERS.USER.QR_CODE, element: <UserQrCode /> },
        ],
      },
    ],
  },
  {
    path: STATIC_BASE_ROUTE,
    children: [
      { path: ROUTERS.STATIC.PRIVACY_POLICY, element: <PrivacyPolicy /> },
      { path: ROUTERS.STATIC.DISCLAIMER, element: <Disclaimer /> },
      { path: ROUTERS.STATIC.RULES, element: <Rules /> },
    ],
  },
]);

export const App = () => {
  return (
    <ChakraProvider theme={BaseTheme}>
      <UIProvider>
        <Preloader />
        <Suspense fallback={<InitLoading />}>
          <RouterProvider router={router} />
        </Suspense>
      </UIProvider>
    </ChakraProvider>
  );
};
